import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "../i18n";
import { X } from "./Icon";

interface Props {
  open: boolean;
  onClose(): void;
  title?: ReactNode;
  children: ReactNode;
  /** narrow = dialogs / confirmations */
  size?: "narrow" | "wide";
  label?: string;
}

/** Bottom sheet on phones, centred dialog on wide screens. Escape and backdrop close it. */
export function Sheet({ open, onClose, title, children, size = "wide", label }: Props) {
  const { d } = useI18n();
  const panel = useRef<HTMLDivElement>(null);
  const prevFocus = useRef<HTMLElement | null>(null);
  // keep the latest onClose without re-running the focus effect (inline handlers change every render)
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    prevFocus.current = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
      if (e.key === "Tab" && panel.current) {
        const f = panel.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    // first field if there is one, otherwise the first button (the close button comes last in the tab order)
    const t = setTimeout(() => {
      const root = panel.current;
      const target = root?.querySelector<HTMLElement>("input:not([type=hidden]), textarea, select") ?? root?.querySelector<HTMLElement>("[data-autofocus], button:not([data-sheet-close])") ?? root?.querySelector<HTMLElement>("button");
      target?.focus();
    }, 60);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
      document.body.style.overflow = overflow;
      prevFocus.current?.focus?.();
    };
  }, [open]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="presentation">
          <motion.div
            className="absolute inset-0 bg-[#0c0418]/75"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label={label ?? (typeof title === "string" ? title : undefined)}
            className={`relative max-h-[92dvh] w-full overflow-y-auto rounded-t-[28px] bg-ground-2 px-5 pt-4 pb-[max(20px,env(safe-area-inset-bottom))] sm:rounded-[28px] sm:pb-6 ${
              size === "narrow" ? "sm:max-w-md" : "sm:max-w-xl"
            }`}
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 420, damping: 34 }}
          >
            <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-ground-4 sm:hidden" aria-hidden />
            <div className="mb-4 flex items-start justify-between gap-3">
              {title ? <h2 className="display text-2xl">{title}</h2> : <span />}
              <button className="icon-btn -mr-1 shrink-0" onClick={onClose} aria-label={d.common.close} data-sheet-close>
                <X size={22} />
              </button>
            </div>
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
